import { FC } from 'react';
import { useWatch } from 'react-hook-form';

import {
  OPTIMIZE_MAX_ALLOWED,
  OPTIMIZE_MIN_ALLOWED,
  OPTIMIZE_PERIOD,
} from 'modules/frequencyManagement/contexts/FormProvider';
import {
  FlexSummaryInset,
  MarginlessBody2Bold,
  ReviewInputRow,
} from 'modules/frequencyManagement/components/optimize/Optimize.styles';
import { Histogram } from 'modules/frequencyManagement/components/histogram/Histogram';
import { Body2NoMargin } from 'modules/frequencyManagement/components/Content.styles';
import { FrequencyPeriod, HistogramData } from 'modules/frequencyManagement/lib/types';
import { LABEL_BY_PERIOD } from 'modules/frequencyManagement/lib/constants';
import { OptimizeReadyReviewTestId } from 'modules/frequencyManagement/components/optimize/Optimize.constants';
import { OptimizeBaseProps } from 'modules/frequencyManagement/components/optimize/Optimize.types';
import { WithCollapsibleReadyContent } from 'modules/frequencyManagement/components/optimize/WithCollapsibleReadyContent';

interface OptimizeReadyReviewProps extends OptimizeBaseProps {
  histogramData: HistogramData;
  lastUpdated: string;
}

export const OptimizeReadyReview: FC<OptimizeReadyReviewProps> = ({
  histogramData,
  lastUpdated,
  rootName,
}) => {
  const minInputName = rootName ? `${rootName}.${OPTIMIZE_MIN_ALLOWED}` : OPTIMIZE_MIN_ALLOWED;
  const maxInputName = rootName ? `${rootName}.${OPTIMIZE_MAX_ALLOWED}` : OPTIMIZE_MAX_ALLOWED;
  const periodSelectName = rootName ? `${rootName}.${OPTIMIZE_PERIOD}` : OPTIMIZE_PERIOD;
  const minAllowed = useWatch({ name: minInputName });
  const maxAllowed = useWatch({ name: maxInputName });
  const period = useWatch({ name: periodSelectName }) as FrequencyPeriod;
  return (
    <WithCollapsibleReadyContent collapsedContent={!!rootName}>
      <FlexSummaryInset data-test={OptimizeReadyReviewTestId}>
        {!rootName && (
          <ReviewInputRow>
            <Body2NoMargin>
              Your optimized range is set at{' '}
              <MarginlessBody2Bold>
                {minAllowed} to {maxAllowed} messages per {LABEL_BY_PERIOD[period]}
              </MarginlessBody2Bold>
              .
            </Body2NoMargin>
          </ReviewInputRow>
        )}
        <Histogram
          lastUpdated={lastUpdated}
          histogramData={histogramData.histogram_data_points}
          refreshPeriod={`${LABEL_BY_PERIOD[period]}ly`}
        />
      </FlexSummaryInset>
    </WithCollapsibleReadyContent>
  );
};
